import React, { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  Timestamp,
  orderBy
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { emailService } from '../../firebase/emailService';
import { RESERVATION_TYPES, RESERVATION_STATUS } from '../../firebase/reservationService';
import { adminService } from '../../firebase/adminService';
import { Calendar, Users, MapPin, Clock, Phone, Mail, Check, X, AlertTriangle, Filter, ChevronDown } from 'lucide-react';

const AdminSpecialRequests = () => {
  const [specialRequests, setSpecialRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [convertReservationData, setConvertReservationData] = useState({
    date: '',
    time: '',
    zone: 'Salón',
  });
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    type: 'all', // 'all', 'waitlist', 'group'
    dateFrom: '',
    dateTo: '',
    minGuests: '',
    maxGuests: ''
  });

  // Cargar solicitudes pendientes al iniciar
  useEffect(() => {
    loadRequests(activeTab);
  }, [activeTab]);

  // Función para cargar las solicitudes según filtro
  const loadRequests = async (status) => {
    setLoading(true);
    setError(null);
    try {
      // Determinar el filtro según el tab seleccionado
      let statusFilter = [RESERVATION_STATUS.PENDING_REVIEW];
      if (status === 'confirmed') {
        statusFilter = [RESERVATION_STATUS.CONFIRMED];
      } else if (status === 'cancelled') {
        statusFilter = [RESERVATION_STATUS.CANCELLED];
      }

      const reservationsRef = collection(db, 'reservations');
      let q = query(
        reservationsRef,
        where('status', 'in', statusFilter)
      );

      // Si no estamos viendo reservas ya procesadas, filtrar por tipo
      if (status === 'pending') {
        q = query(
          reservationsRef,
          where('reservationType', 'in', [RESERVATION_TYPES.WAITLIST, RESERVATION_TYPES.GROUP]),
          where('status', 'in', statusFilter)
        );
      } else {
        // Para las confirmadas o canceladas, necesitamos también incluir las que tienen origen 'special'
        q = query(
          reservationsRef,
          where('status', 'in', statusFilter),
          where('source', '==', 'special')
        );
      }

      const snapshot = await getDocs(q);
      const requests = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt ? new Date(doc.data().createdAt) : new Date()
      }));

      setSpecialRequests(requests);
    } catch (error) {
      console.error('Error al cargar solicitudes especiales:', error);
      setError('Error al cargar los datos. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  // Aplicar filtros adicionales a las solicitudes ya cargadas
  const applyFilters = () => {
    let filtered = [...specialRequests];

    // Filtrar por tipo de solicitud
    if (filters.type !== 'all') {
      filtered = filtered.filter(req => 
        req.reservationType === filters.type
      );
    }

    // Filtrar por fecha (rango)
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filtered = filtered.filter(req => {
        const parts = req.date.split('/');
        const reservationDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
        return reservationDate >= fromDate;
      });
    }

    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      filtered = filtered.filter(req => {
        const parts = req.date.split('/');
        const reservationDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
        return reservationDate <= toDate;
      });
    }

    // Filtrar por número de comensales
    if (filters.minGuests) {
      filtered = filtered.filter(req => req.guests >= parseInt(filters.minGuests));
    }

    if (filters.maxGuests) {
      filtered = filtered.filter(req => req.guests <= parseInt(filters.maxGuests));
    }

    return filtered;
  };

  // Función para convertir una solicitud en reserva normal
  const convertToNormalReservation = async () => {
    if (!selectedRequest) return;
    
    setProcessingAction(true);
    try {
      const requestRef = doc(db, 'reservations', selectedRequest.id);
      
      // Verificar que tengamos los datos mínimos necesarios
      if (!convertReservationData.date || !convertReservationData.time) {
        throw new Error("Faltan datos necesarios para crear la reserva");
      }
      
      // Formatear la fecha si es necesario
      let formattedDate = convertReservationData.date;
      if (formattedDate.includes('-')) {
        // Convertir de YYYY-MM-DD a DD/MM/YYYY
        const dateParts = formattedDate.split('-');
        formattedDate = `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;
      }
      
      // Preparar los datos de la reserva
      const reservationData = {
        date: formattedDate,
        time: convertReservationData.time,
        zone: convertReservationData.zone,
        status: RESERVATION_STATUS.CONFIRMED,
        reservationType: RESERVATION_TYPES.NORMAL, // Convertir a reserva normal
        source: 'special', // Marcar como origen especial
        confirmedAt: Timestamp.fromDate(new Date())
      };
  
      // Actualizar la reserva en Firebase
      await updateDoc(requestRef, reservationData);
      
      // Enviar email de confirmación
      try {
        await emailService.sendReservationStatusEmail(
          {...selectedRequest, ...reservationData},
          RESERVATION_STATUS.CONFIRMED
        );
      } catch (emailError) {
        console.error("Error enviando email de confirmación:", emailError);
        // Continuamos con el proceso aunque falle el envío de email
      }
      
      // Actualizar UI
      setModalOpen(false);
      setSelectedRequest(null);
      setConfirmModalOpen(false);
      loadRequests(activeTab);
      
      alert('Solicitud convertida a reserva con éxito. Puede asignar mesas desde el panel de reservas normal.');
    } catch (error) {
      console.error('Error al convertir solicitud a reserva:', error);
      alert('Error al convertir la solicitud: ' + error.message);
    } finally {
      setProcessingAction(false);
    }
  };

  // Ya no necesitamos verificar capacidad de mesas porque no las asignamos aquí

  // Cuando se abre el modal para confirmar una solicitud
  const handlePrepareConversion = async () => {
    if (!selectedRequest) return;
    
    // Obtener fecha actual o de la solicitud si existe
    const currentDate = selectedRequest.date 
      ? selectedRequest.date.split('/').reverse().join('-')
      : new Date().toISOString().split('T')[0];
    
    setConvertReservationData({
      date: currentDate,
      time: selectedRequest.time || '20:00', // Valor por defecto
      zone: 'Salón'
    });
    
    setConfirmModalOpen(true);
  };

  // Formatear fecha para mostrar
  const formatDate = (date) => {
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Utilizar los filtros para mostrar las solicitudes
  const filteredRequests = applyFilters();

  // Ya no necesitamos manejar selección de mesas

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">Gestión de Solicitudes Especiales</h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 relative">
          <span className="block sm:inline">{error}</span>
          <button
            onClick={() => setError(null)}
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
          >
            <X size={16} />
          </button>
        </div>
      )}
      
      {/* Tabs de filtrado */}
      <div className="border-b mb-6 flex justify-between items-center">
        <nav className="flex">
          <button
            className={`py-2 px-4 font-medium ${activeTab === 'pending' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('pending')}
          >
            Pendientes
          </button>
          <button
            className={`py-2 px-4 font-medium ${activeTab === 'confirmed' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('confirmed')}
          >
            Confirmadas
          </button>
          <button
            className={`py-2 px-4 font-medium ${activeTab === 'cancelled' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('cancelled')}
          >
            Canceladas
          </button>
        </nav>
        
        <button
          onClick={() => setFilterModalOpen(true)}
          className="flex items-center bg-gray-100 hover:bg-gray-200 py-1 px-3 rounded text-sm"
        >
          <Filter size={16} className="mr-1" />
          Filtros
        </button>
      </div>
      
      {/* Tabla de solicitudes */}
      {loading ? (
        <div className="flex justify-center p-8">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="text-center p-8 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No hay solicitudes {activeTab === 'pending' ? 'pendientes' : activeTab === 'confirmed' ? 'confirmadas' : 'canceladas'}</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg shadow">
          <table className="min-w-full bg-white border-collapse">
            <thead className="bg-gray-50">
              <tr>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Personas</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recibida</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map((request) => (
                <tr key={request.id} className="border-t hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      request.reservationType === RESERVATION_TYPES.WAITLIST
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {request.reservationType === RESERVATION_TYPES.WAITLIST ? 'Lista de espera' : 'Grupo'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="font-medium text-gray-900">{request.customer?.firstName} {request.customer?.lastName}</div>
                    <div className="text-xs text-gray-500">{request.customer?.email}</div>
                  </td>
                  <td className="py-3 px-4">
                    <div>{request.date}</div>
                    {request.time && <div className="text-xs text-gray-500">{request.time}h</div>}
                  </td>
                  <td className="py-3 px-4">{request.guests} personas</td>
                  <td className="py-3 px-4">{formatDate(request.createdAt)}</td>
                  <td className="py-3 px-4">
                    <div className="flex flex-wrap gap-2">
                      <button
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        onClick={() => {
                          setSelectedRequest(request);
                          setModalOpen(true);
                        }}
                      >
                        Ver detalles
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Modal de detalles */}
      {modalOpen && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold">Detalles de la Solicitud</h2>
                <button
                  className="text-gray-500 hover:text-gray-700"
                  onClick={() => {
                    setModalOpen(false);
                    setSelectedRequest(null);
                  }}
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                    selectedRequest.reservationType === RESERVATION_TYPES.WAITLIST
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {selectedRequest.reservationType === RESERVATION_TYPES.WAITLIST ? 'Lista de espera' : 'Reserva de grupo'}
                  </span>
                  
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                    activeTab === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    activeTab === 'confirmed' ? 'bg-green-100 text-green-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {selectedRequest.status}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <div className="text-sm text-gray-500">Cliente</div>
                    <div className="font-medium">
                      {selectedRequest.customer?.firstName} {selectedRequest.customer?.lastName}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Fecha solicitada</div>
                    <div className="font-medium">
                      {selectedRequest.date} {selectedRequest.time && `${selectedRequest.time}h`}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Email</div>
                    <div className="font-medium flex items-center gap-1">
                      <Mail size={16} className="text-gray-400" />
                      {selectedRequest.customer?.email}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Teléfono</div>
                    <div className="font-medium flex items-center gap-1">
                      <Phone size={16} className="text-gray-400" />
                      {selectedRequest.customer?.phone}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Personas</div>
                    <div className="font-medium flex items-center gap-1">
                      <Users size={16} className="text-gray-400" />
                      {selectedRequest.guests}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Fecha de solicitud</div>
                    <div className="font-medium flex items-center gap-1">
                      <Calendar size={16} className="text-gray-400" />
                      {formatDate(selectedRequest.createdAt)}
                    </div>
                  </div>
                </div>
                
                {/* Comentarios del cliente */}
                {selectedRequest.customer?.comments && (
                  <div className="mt-4">
                    <div className="text-sm text-gray-500">Comentarios</div>
                    <div className="bg-gray-50 p-3 rounded mt-1">
                      {selectedRequest.customer.comments}
                    </div>
                  </div>
                )}
                
                {/* Alergias */}
                {selectedRequest.customer?.allergies && (
                  <div className="mt-4">
                    <div className="text-sm text-gray-500 flex items-center">
                      <AlertTriangle size={16} className="text-red-500 mr-1" />
                      Alergias/Intolerancias
                    </div>
                    <div className="bg-red-50 p-3 rounded mt-1 text-red-800">
                      {selectedRequest.customer.allergies}
                    </div>
                  </div>
                )}
                
                {/* Información adicional según estado */}
                {activeTab !== 'pending' && (
                  <div className="mt-6 pt-4 border-t">
                    <h3 className="font-medium mb-2">Estado de la solicitud</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {selectedRequest.confirmedAt && (
                        <div>
                          <div className="text-sm text-gray-500">Confirmada</div>
                          <div className="font-medium">
                            {formatDate(new Date(selectedRequest.confirmedAt.seconds * 1000))}
                          </div>
                        </div>
                      )}
                      {selectedRequest.cancelledAt && (
                        <div>
                          <div className="text-sm text-gray-500">Cancelada</div>
                          <div className="font-medium">
                            {formatDate(new Date(selectedRequest.cancelledAt.seconds * 1000))}
                          </div>
                        </div>
                      )}
                      {selectedRequest.tables && selectedRequest.tables.length > 0 && (
                        <div className="col-span-2">
                          <div className="text-sm text-gray-500">Mesas asignadas</div>
                          <div className="font-medium">
                            {selectedRequest.tables.join(', ')}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Acciones disponibles según el estado */}
              {activeTab === 'pending' && (
                <div className="flex justify-end gap-2 border-t pt-4">
                  <button
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                    onClick={() => setModalOpen(false)}
                  >
                    Cerrar
                  </button>
                  <button
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    onClick={() => {
                      setModalOpen(false);
                      handlePrepareConversion();
                    }}
                  >
                    Convertir a reserva
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de confirmación para convertir a reserva */}
      {confirmModalOpen && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-xl w-full">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold">Convertir a Reserva</h2>
                <button
                  className="text-gray-500 hover:text-gray-700"
                  onClick={() => setConfirmModalOpen(false)}
                  disabled={processingAction}
                >
                  <X size={20} />
                </button>
              </div>
              
              <p className="mb-4 text-gray-600">
                Confirma los detalles para crear una reserva a partir de la solicitud de 
                <span className="font-medium"> {selectedRequest.customer?.firstName} {selectedRequest.customer?.lastName}</span>
              </p>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
                  <input 
                    type="date"
                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={convertReservationData.date}
                    onChange={(e) => setConvertReservationData({
                      ...convertReservationData,
                      date: e.target.value
                    })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hora</label>
                  <input 
                    type="time"
                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={convertReservationData.time}
                    onChange={(e) => setConvertReservationData({
                      ...convertReservationData,
                      time: e.target.value
                    })}
                    required
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Zona</label>
                  <select
                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={convertReservationData.zone}
                    onChange={(e) => setConvertReservationData({
                      ...convertReservationData,
                      zone: e.target.value
                    })}
                    required
                  >
                    <option value="Salón">Salón</option>
                    <option value="Terraza">Terraza</option>
                    <option value="Privado">Privado</option>
                  </select>
                </div>
              </div>
              
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                <div className="flex items-center mb-1">
                  <AlertTriangle size={18} className="text-blue-500 mr-2" />
                  <span className="text-sm font-medium text-blue-700">Nota importante</span>
                </div>
                <p className="text-sm text-blue-600">
                  Esta acción convertirá la solicitud en una reserva normal. Las mesas se asignarán posteriormente desde el panel de reservas.
                </p>
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                  onClick={() => setConfirmModalOpen(false)}
                  disabled={processingAction}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:hover:bg-green-600"
                  onClick={convertToNormalReservation}
                  disabled={!convertReservationData.date || !convertReservationData.time || processingAction}
                >
                  {processingAction ? 'Procesando...' : 'Crear Reserva'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de filtros */}
      {filterModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold">Filtros de Búsqueda</h2>
                <button
                  className="text-gray-500 hover:text-gray-700"
                  onClick={() => setFilterModalOpen(false)}
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Solicitud</label>
                  <select
                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={filters.type}
                    onChange={(e) => setFilters({...filters, type: e.target.value})}
                  >
                    <option value="all">Todas</option>
                    <option value={RESERVATION_TYPES.WAITLIST}>Lista de espera</option>
                    <option value={RESERVATION_TYPES.GROUP}>Grupo</option>
                  </select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Desde Fecha</label>
                    <input 
                      type="date"
                      className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={filters.dateFrom}
                      onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hasta Fecha</label>
                    <input 
                      type="date"
                      className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={filters.dateTo}
                      onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mínimo Comensales</label>
                    <input 
                      type="number"
                      className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={filters.minGuests}
                      onChange={(e) => setFilters({...filters, minGuests: e.target.value})}
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Máximo Comensales</label>
                    <input 
                      type="number"
                      className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={filters.maxGuests}
                      onChange={(e) => setFilters({...filters, maxGuests: e.target.value})}
                      min="1"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => {
                    setFilters({
                      type: 'all',
                      dateFrom: '',
                      dateTo: '',
                      minGuests: '',
                      maxGuests: ''
                    });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Limpiar
                </button>
                <button
                  onClick={() => setFilterModalOpen(false)}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Aplicar Filtros
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSpecialRequests;